import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// 자주 사용하는 페이지는 그대로 import
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import AdminModePanel from './components/AdminModePanel';
import GroupOwnerPanel from './components/GroupOwnerPanel';

// 나머지는 lazy 로드
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));

// 인증이 필요한 라우트를 위한 래퍼 컴포넌트
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();
  
  // 로딩 중일 때는 로딩 표시
  if (isLoading) {
    return <div>로딩 중...</div>;
  }
  
  return isLoggedIn ? children : <Navigate to="/" replace />;
};

// 인증 상태에 따른 리디렉션 처리
const AuthRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();
  
  // 로딩 중일 때는 로딩 표시
  if (isLoading) {
    return <div>로딩 중...</div>;
  }
  
  // 이미 로그인되어 있으면 홈 페이지로 리디렉션
  return isLoggedIn ? <Navigate to="/home" replace /> : children;
};

// 관리자 모드 패널 컴포넌트
const AdminModePanelWrapper = () => {
  const { isAdmin, isAdminMode, originalAdminUser, user } = useAuth();
  
  // 원래 어드민이거나 어드민 모드인 경우 패널 표시
  if (isAdmin() || isAdminMode || originalAdminUser) {
    return <AdminModePanel />;
  }
  
  return null;
};

// 그룹 관리자 패널 컴포넌트
const GroupOwnerPanelWrapper = () => {
  const { isGroupOwner, getCurrentUser, user, originalAdminUser, isAdminMode, impersonatedUser } = useAuth();
  const path = window.location.pathname;
  
  // // 홈페이지나 대시보드에서만 렌더링
  // if (path !== '/home' && path !== '/dashboard') {
  //   return null;
  // }
  
  // 현재 사용자가 그룹 관리자인지 확인 (is_group_owner 필드는 user 객체 밖에 있음)
  const currentUser = getCurrentUser();
  
  // 어드민이 가장한 경우 is_group_owner 확인 
  // (impersonatedUser 객체에 is_group_owner가 별도 필드로 있거나 owned_groups를 확인)
  if (isGroupOwner() || (impersonatedUser && (impersonatedUser.is_group_owner))) {
    return <GroupOwnerPanel />;
  }
  
  return null;
};

const App = () => {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AuthProvider>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={
                <AuthRoute>
                  <LoginPage />
                </AuthRoute>
              } />
              
              <Route path="/home" element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } />
              
              <Route path="/profile" element={
                <ProtectedRoute>
                    <ProfilePage />
                 </ProtectedRoute>
              } />
              
              <Route path="/group" element={
                <ProtectedRoute>
                  <TeamPage />
                </ProtectedRoute>
              } />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              
              <Route path="/preview" element={<PreviewPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              
              {/* 정의되지 않은 경로는 홈으로 리디렉션 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          
          {/* 관리자 모드 패널 - 모든 페이지에서 사용 가능 */}
          <AdminModePanelWrapper />
          
          {/* 그룹 관리자 패널 - 홈페이지와 대시보드에서만 사용 가능 */}
          <GroupOwnerPanelWrapper />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App; 